// @spec AI-STR-001
// @spec AI-STR-006
// @spec AI-STR-010
// @spec AI-STR-011
// @spec AI-STR-014
// @spec AI-STR-022
// AI Hub landing page - central navigation for all AI features

import { useAuth } from '@clerk/clerk-react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Bot,
  ChevronDown,
  ChevronUp,
  FileSearch,
  FileText,
  GraduationCap,
  History,
  Lightbulb,
  Loader2,
  MessageSquare,
  Paperclip,
  Plus,
  Shield,
  Sparkles,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ChatInput,
  ChatMessageList,
  ChatWelcome,
  useCreateConversation,
  useConversation,
  useSendMessage,
  useChatStore,
} from '@/features/ai/chat';
import { ConversationHistory } from '@/features/ai/chat/components/ConversationHistory';
import type { ActionSuggestEvent, DataResultEvent } from '@/features/ai/chat/types';
import { AIDocumentUploadZone } from '@/features/ai/documents/components/document-upload-zone';
import { DocumentSelector } from '@/features/ai/documents/components/document-selector';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ai/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.aiAdvisor'),
  }),
  component: AIHubPage,
});

interface FeatureCardProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

function FeatureCard({ to, icon, title, description }: FeatureCardProps) {
  return (
    <Link to={to} className="block">
      <Card className="h-full transition-colors hover:border-primary hover:bg-accent/50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              {icon}
            </div>
            <CardTitle className="text-lg">{title}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-sm">{description}</CardDescription>
        </CardContent>
      </Card>
    </Link>
  );
}

/**
 * Inline AI chat component for the AI Advisor hub page.
 * Uses the global chat store so state is shared with the floating chat button.
 */
function InlineAIChat() {
  const { getToken } = useAuth();
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  // Global chat store state
  const activeConversationId = useChatStore.use.activeConversationId();
  const setActiveConversation = useChatStore.use.setActiveConversation();
  const isStreaming = useChatStore.use.isStreaming();
  const streamingContent = useChatStore.use.streamingContent();
  const clearStreamContent = useChatStore.use.clearStreamContent();
  const pendingActions = useChatStore.use.pendingActions();
  const removePendingAction = useChatStore.use.removePendingAction();
  const dataResults = useChatStore.use.dataResults();

  // View mode: chat or history
  const [viewMode, setViewMode] = useState<'chat' | 'history'>('chat');

  // Document RAG state
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [isDocsPanelOpen, setIsDocsPanelOpen] = useState(false);

  // Optimistic user message
  const [optimisticUserMessage, setOptimisticUserMessage] = useState<string | null>(null);

  // Conversation hooks
  const { data: conversation, isLoading: isLoadingConversation } =
    useConversation(activeConversationId);

  const { mutateAsync: createConversation, isPending: isCreating } = useCreateConversation({
    onSuccess: (newConversation) => {
      setActiveConversation(newConversation.id);
    },
  });

  const { sendMessage, isLoading: isSending } = useSendMessage({
    conversationId: activeConversationId,
    documentIds: selectedDocumentIds.length > 0 ? selectedDocumentIds : undefined,
    onMessageComplete: () => {
      clearStreamContent();
    },
  });

  // Clear optimistic message once server has it
  useEffect(() => {
    if (optimisticUserMessage && conversation?.messages?.length) {
      const found = conversation.messages.some(
        (m) => m.role === 'user' && m.content === optimisticUserMessage
      );
      if (found) {
        setOptimisticUserMessage(null);
      }
    }
  }, [conversation?.messages, optimisticUserMessage]);

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      setActiveConversation(conversationId);
      setViewMode('chat');
    },
    [setActiveConversation]
  );

  const handleNewConversation = useCallback(() => {
    setActiveConversation(null);
    clearStreamContent();
    setOptimisticUserMessage(null);
    setViewMode('chat');
  }, [setActiveConversation, clearStreamContent]);

  const addUploadedDocument = useCallback((docId: string) => {
    setSelectedDocumentIds((prev) => (prev.includes(docId) ? prev : [...prev, docId]));
  }, []);

  const handleSendMessage = useCallback(
    async (content: string) => {
      setOptimisticUserMessage(content);

      if (!activeConversationId) {
        // Create conversation first, then send
        const newConversation = await createConversation({});
        setTimeout(() => {
          void (async () => {
            const convId = newConversation.id;
            if (!convId) return;

            const token = await getToken();
            if (!token) return;

            const baseUrl = import.meta.env.VITE_API_URL;
            const url = `${baseUrl}/ai/conversations/${convId}/messages`;

            const response = await fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                Accept: 'text/event-stream',
              },
              body: JSON.stringify({
                content,
                ...(selectedDocumentIds.length > 0 && { documentIds: selectedDocumentIds }),
              }),
            });

            if (response.ok && response.body) {
              const reader = response.body.getReader();
              const decoder = new TextDecoder();
              useChatStore.setState({ isStreaming: true, pendingActions: [], dataResults: [] });

              let currentEvent = '';

              for (;;) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');

                for (const line of lines) {
                  if (line.startsWith('event:')) {
                    currentEvent = line.slice(7).trim();
                  } else if (line.startsWith('data:')) {
                    try {
                      const data = JSON.parse(line.slice(5).trim()) as Record<string, unknown>;

                      if (currentEvent === 'content_delta' && data.delta) {
                        const delta = data.delta as string;
                        useChatStore.setState((state) => ({
                          streamingContent: state.streamingContent + delta,
                        }));
                      } else if (currentEvent === 'action_suggest') {
                        useChatStore.setState((state) => ({
                          pendingActions: [
                            ...state.pendingActions,
                            data as unknown as ActionSuggestEvent,
                          ],
                        }));
                      } else if (currentEvent === 'data_result') {
                        useChatStore.setState((state) => ({
                          dataResults: [
                            ...state.dataResults,
                            data as unknown as DataResultEvent,
                          ],
                        }));
                      }

                      currentEvent = '';
                    } catch {
                      // Ignore parse errors
                    }
                  }
                }
              }

              useChatStore.setState({ isStreaming: false });
              void queryClient.invalidateQueries({
                queryKey: ['ai', 'conversations', convId],
              });
              void queryClient.invalidateQueries({
                queryKey: ['ai', 'conversations'],
              });
            }
          })();
        }, 100);
      } else {
        void sendMessage(content);
      }
    },
    [activeConversationId, createConversation, sendMessage, getToken, queryClient, selectedDocumentIds]
  );

  const messages = useMemo(() => {
    const serverMessages = conversation?.messages ?? [];
    if (!optimisticUserMessage) return serverMessages;
    const alreadyInList = serverMessages.some(
      (m) => m.role === 'user' && m.content === optimisticUserMessage
    );
    if (alreadyInList) return serverMessages;
    return [
      ...serverMessages,
      {
        id: `optimistic-${String(Date.now())}`,
        role: 'user' as const,
        content: optimisticUserMessage,
        createdAt: new Date().toISOString(),
      },
    ];
  }, [conversation?.messages, optimisticUserMessage]);

  const isLoading = isLoadingConversation || isCreating || isSending;
  const showWelcome = !activeConversationId && !isStreaming && viewMode === 'chat';
  const showHistory = viewMode === 'history';

  return (
    <Card className="flex flex-col h-[600px]">
      {/* Header bar */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-b">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setViewMode(viewMode === 'history' ? 'chat' : 'history'); }}
            aria-label={showHistory ? 'Back to chat' : 'Conversation history'}
          >
            <History className={`h-4 w-4 ${showHistory ? 'text-primary' : ''}`} />
          </Button>
          <span className="text-sm font-medium truncate">
            {showHistory ? t('ai.conversationHistory') : (conversation?.title ?? t('ai.aiAssistant'))}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleNewConversation}
          disabled={isLoading}
        >
          <Plus className="h-4 w-4 mr-1" />
          {t('ai.new')}
        </Button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {/* Messages area */}
        <div className="flex-1 min-h-0">
          {showHistory ? (
            <ConversationHistory
              onSelectConversation={handleSelectConversation}
              className="h-full"
            />
          ) : isLoadingConversation && activeConversationId ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : showWelcome ? (
            <ScrollArea className="h-full">
              <ChatWelcome onSendPrompt={handleSendMessage} />
            </ScrollArea>
          ) : (
            <ChatMessageList
              messages={messages}
              conversationId={activeConversationId ?? undefined}
              isStreaming={isStreaming}
              streamingContent={streamingContent}
              pendingActions={pendingActions}
              onActionResolved={removePendingAction}
              dataResults={dataResults}
              onSendFollowUp={handleSendMessage}
              className="h-full"
            />
          )}
        </div>

        {/* Reference Documents (RAG) panel - hidden in history view */}
        {!showHistory && <div className="flex-shrink-0 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="w-full flex items-center justify-between px-4 py-2 h-auto"
            onClick={() => { setIsDocsPanelOpen((prev) => !prev); }}
          >
            <span className="flex items-center gap-2 text-xs text-muted-foreground">
              <Paperclip className="h-3.5 w-3.5" />
              {t('ai.referenceDocuments')}
              {selectedDocumentIds.length > 0 ? (
                <span className="text-xs font-medium text-foreground">
                  ({selectedDocumentIds.length})
                </span>
              ) : (
                <span className="hidden sm:inline"> — {t('ai.searchSpecificDocs')}</span>
              )}
            </span>
            {isDocsPanelOpen ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </Button>
          {isDocsPanelOpen && (
            <div className="px-4 pb-3 space-y-3">
              <p className="text-xs text-muted-foreground">
                {t('ai.uploadOrSelectDocuments')}
              </p>
              <AIDocumentUploadZone
                compact
                onUploadComplete={(docId) => {
                  if (docId) {
                    addUploadedDocument(docId);
                  }
                }}
              />
              <DocumentSelector
                selectedIds={selectedDocumentIds}
                onSelectionChange={setSelectedDocumentIds}
                maxSelections={10}
              />
            </div>
          )}
        </div>}

        {/* Chat input - hidden in history view */}
        {!showHistory && (
          <ChatInput
            onSend={handleSendMessage}
            isLoading={isLoading || isStreaming}
            disabled={isCreating}
            placeholder={t('ai.askAnything')}
          />
        )}
      </div>
    </Card>
  );
}


function AIHubPage() {
  const { t } = useTranslation('common');
  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <header className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Bot className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-semibold text-foreground">{t('ai.advisorTitle')}</h1>
          <p className="mt-2 text-muted-foreground">
            {t('ai.advisorDescription')}
          </p>
        </header>

        {/* Inline AI Chat */}
        <InlineAIChat />

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              {t('ai.quickActions')}
            </CardTitle>
            <CardDescription>
              {t('ai.quickActionsDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <FeatureCard
                to="/ai/documents"
                icon={<FileText className="h-5 w-5" />}
                title={t('ai.documentsTitle')}
                description={t('ai.documentsDescription')}
              />
              <FeatureCard
                to="/ai/scans"
                icon={<FileSearch className="h-5 w-5" />}
                title={t('ai.complianceScansTitle')}
                description={t('ai.complianceScansDescription')}
              />
              <FeatureCard
                to="/ai/insights"
                icon={<Lightbulb className="h-5 w-5" />}
                title={t('ai.insightsTitle')}
                description={t('ai.insightsDescription')}
              />
              <FeatureCard
                to="/ai/quality"
                icon={<Shield className="h-5 w-5" />}
                title={t('ai.dataQualityTitle')}
                description={t('ai.dataQualityDescription')}
              />
              <FeatureCard
                to="/ghg/setup"
                icon={<GraduationCap className="h-5 w-5" />}
                title={t('ai.ghgSetupTitle')}
                description={t('ai.ghgSetupDescription')}
              />
              <FeatureCard
                to="/ai/onboarding/esg"
                icon={<GraduationCap className="h-5 w-5" />}
                title={t('ai.esgOnboardingTitle')}
                description={t('ai.esgOnboardingDescription')}
              />
              <FeatureCard
                to="/ai/onboarding/nis2"
                icon={<GraduationCap className="h-5 w-5" />}
                title={t('ai.nis2OnboardingTitle')}
                description={t('ai.nis2OnboardingDescription')}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
